import React, { useEffect, useState, FormEvent, useLayoutEffect, ReactElement } from 'react';
import './OfferColumn.css';
import { transformHTMLParserLinks } from '../../../utils/rewriteLink/rewriteLink';
import axios from 'axios';
import SessionStorage from '../../../utils/SessionStorage';
import { CompareApi } from '../../../utils/compare-api/CompareApi';
import { acquisitionApiRequest } from '../../../utils/acquisition-api/AcquisitionApi';
import { usePushdownContext } from '../../../providers/Pushdown/PushdownProvider';
import { getParameterByName, wrapTrademarkSuperscript } from '../../../utils/cob-utils';
import { IOfferModuleCF } from '../../../types/shared/OfferModule';
import { clearSessionStorageVariablesForAcquisitionFlow } from '../../../utils/acquisition-utils';
import { createAnnualFeeMessage } from '../../Offer/Offer';
import { ISlideValidState } from '../MultiOfferBlock';
import { Button, Banner } from '../../../importCL';

interface IOfferColumn {
    offerData: Record<string, any>;
    index: number;
    equalizeHeightCallback: () => void;
    parentProps: Record<string, any>;
    showSlides: Array<ISlideValidState>;
    setShowSlides: React.Dispatch<React.SetStateAction<Array<ISlideValidState>>>;
    isDisabledCTA: boolean;
    handleDisableCTA: any;
}

export const OfferColumn = ({ offerData, index, parentProps, equalizeHeightCallback, showSlides, setShowSlides, isDisabledCTA, handleDisableCTA }: IOfferColumn) => {
    const [ratesAndFees, setRatesAndFees] = useState<string>('');
    const { showPushdown } = usePushdownContext();
    const authorViewMode = window.location.search.includes('?wcmmode=disabled')
        ? '&wcmmode=disabled'
        : '';

    const [termsUrl, setTermsUrl] = useState<string>('');

    const redirect = (redirectUrl: string, errorCode: string = '') => {
        let redirect = '';
        if (errorCode) {
            const newUrl = `${window.location.origin}${redirectUrl}${window.location.search}`;
            const redirectWithError = new URL(newUrl);
            redirectWithError.searchParams.set('errorCode', parentProps.errorCode);
            redirect = redirectWithError.href;
        } else {
            redirect = redirectUrl + window.location.search;
        }
        window.location.assign(redirect);
    };

    const getOfferInformationApi = async (programCode: string) => {
        const partnerId = getParameterByName('C1BPartnerID') || '';
        const params = `${programCode}${partnerId === '' ? '' : `&partnerId=${partnerId}`}`;

        await axios.get(`/services/appoffermodule?programCode=${params}`)
            .then(response => response.data as IOfferModuleCF)
            .then(data => {
                SessionStorage.setObject('offerContent', data ?? {});
                SessionStorage.setObject('cardInfo', offerData?.CardDesign?.Cards ?? {});
                redirect(parentProps.fullAppUrl);
            })
            .catch((err) => {
                console.debug('Error w/ retrieving program code in OfferColumn: ' + err);
                redirect('/pre-qualification/data-entry/index?errorCode=noTncError');
                return;
            });
    };

    const sendAcceptedProgramCodeApi = (programCode: string) => {
        acquisitionApiRequest(
            'send-accepted-programCode',
            { body: { programCode: programCode }, method: 'POST' },
        ).then((response) => {
            if (response.status === 200) {
                SessionStorage.set('selectedProgramCode', programCode);
                SessionStorage.remove('cardInfo');
                getOfferInformationApi(programCode);
            } else if (response.status === 408) {
                clearSessionStorageVariablesForAcquisitionFlow();
                redirect(parentProps?.dataEntryUrl, parentProps?.errorCode);
            }

        }).catch((err) => {
            console.error(err);
            clearSessionStorageVariablesForAcquisitionFlow();
            redirect(parentProps.dataEntryUrl, parentProps.errorCode);
        });
    };

    const handleOnClick = (e: FormEvent<HTMLButtonElement>) => {
        handleDisableCTA();
        e.preventDefault();
        if (!SessionStorage.verifyNotEmpty('initResponse')) {
            CompareApi.initializeApi().then(() => {
                sendAcceptedProgramCodeApi(offerData.programCode);
            }).catch(() => {
                showPushdown({
                    color: 'error',
                    header: 'Something went wrong',
                    description: 'Unable to initialize session with the remote server. Please try again later.',
                });

            });

        } else {
            sendAcceptedProgramCodeApi(offerData.programCode);
        }
    };

    const fetchComplexAnnualFee = () => {
        if (!parentProps.complexAnnualFeeContentFragmentPath || !offerData.programCode) {
            return;
        }
        
        axios.get(`/services/ComplexAnnualFeeServlet.path=${parentProps.complexAnnualFeeContentFragmentPath}.programCode=${offerData.programCode}`)
            .then(res => res.data)
            .then(data => {
                if (data?.Error) {
                    // eslint-disable-next-line no-console
                    console.log(data.Error);
                }

                if (data?.complexAnnualFeeText) {
                    setRatesAndFees(data.complexAnnualFeeText);
                }
            })
            .catch(err => {
                // eslint-disable-next-line no-console
                console.log(err);
            });
    };

    const findAndSetTermUrl = () => {
        const programCode = offerData.programCode;
        const servletUrl = `/services/termfinder?programCode=${programCode}`;
        axios
            .get(servletUrl)
            .then(response => {
                if (response.data !== '' && response.data !== undefined) {
                    setTermsUrl(
                        `${response.data.termsPath}${parentProps.isInEditor || authorViewMode ? '.html' : ''}?pc=${programCode}${authorViewMode}`,
                    );
                }
            })
            .catch(err => {
                console.error(err);
                setShowSlides((curr) => {
                    const updatedArr = [...curr];
                    updatedArr[index - 1] = {
                        evaluated: true,
                        show: false,
                    };
                    return updatedArr;
                });
            });
    };

    useEffect(() => {
        setRatesAndFees(createAnnualFeeMessage(offerData.programRates, { ...parentProps }));
        fetchComplexAnnualFee();
        findAndSetTermUrl();
    }, []);

    useLayoutEffect(() => {
        if (ratesAndFees && !showSlides.filter(state => !state.evaluated).length && showSlides[index-1].show) {
            equalizeHeightCallback();
        }
    }, [ratesAndFees, showSlides]);

    useEffect(() => {
        if (termsUrl) {
            fetch(termsUrl, { redirect: 'error' })
                .then(() => {
                    setShowSlides((curr) => {
                        const updatedArr = [...curr];
                        updatedArr[index - 1] = {
                            evaluated: true,
                            show: true,
                        };
                        return updatedArr;
                    });
                }).catch(() => {
                    setShowSlides((curr) => {
                        const updatedArr = [...curr];
                        updatedArr[index - 1] = {
                            evaluated: true,
                            show: false,
                        };
                        return updatedArr;
                    });
                });
        }
    }, [termsUrl]);

    return (
        <article className='OfferColumn'>
            <div className='OfferColumn__flagWrapper'>
                {offerData.isMostPopular &&
                    <Banner className='OfferColumn__flag' alt={parentProps.mostPopularFlagAltText}> Most
                        Popular </Banner>}
            </div>
            <div className='OfferColumn__container'>

                <img loading='lazy' id='card-image' className='OfferColumn__cardImg' src={offerData.offerImage}
                    alt={offerData.offerImageAltText} />
                {offerData.offerTitle &&
                    <h3 className='OfferColumn__cardTitle'>{transformHTMLParserLinks(offerData.offerTitle)}<sup className='OfferColumn__sup'>{index}</sup></h3>}
                <Button className='OfferColumn__cta'
                        disabled={isDisabledCTA} 
                        onClick={handleOnClick}
                        size='medium'>
                        {transformHTMLParserLinks(wrapTrademarkSuperscript(parentProps?.applyNowCTAText)) as unknown as ReactElement}
                </Button>
                <div className='OfferColumn__strikethroughRatesTitle'>
                    <hr className='OfferColumn__hr' />
                    <h4 className='OfferColumn__ratesTitle'>{transformHTMLParserLinks(parentProps.rateTitle)}<sup>{index}</sup></h4>
                </div>
                <div className='OfferColumn__ratesContainer'>
                    <p>{`${transformHTMLParserLinks(offerData.programRates.CalculatedPurchaseRate)}% Variable APR`}</p>
                    {ratesAndFees.startsWith('<p>') ? (
                        <>{transformHTMLParserLinks(ratesAndFees)}</>
                    ) : (
                        <p>{transformHTMLParserLinks(ratesAndFees)}</p>
                    )}
                    <p>{transformHTMLParserLinks(offerData.creditLineText)}</p>
                </div>
                <div className='OfferColumn__strikethroughRewardsTitle'>
                    <hr className='OfferColumn__hr' />
                    <h4 className='OfferColumn__rewards'>{transformHTMLParserLinks(parentProps?.rewardTitle)}<sup>{index}</sup>
                    </h4>
                </div>
                <div className='OfferColumn__rewardsContainer' >
                    {offerData.bulletText?.map((el: any, i: any) => {
                        return (
                            <p key={i}> {transformHTMLParserLinks(el)}</p>
                        );
                    })}
                </div>
                <div className='OfferColumn__terms'>
                    <a className='OfferColumn__termsLink' href={termsUrl} target='_blank' rel='noopener noreferrer'><sup>{index}</sup>Terms & Conditions</a>
                </div>
            </div>

        </article>
    );
};
